<i18n>
{
  "en": {
    "agent": {
      "id": "ID",
      "url": "URL",
      "token": "Token",
      "status": "Status",
      "location": "Location",
      "version": "Agent version",
      "statusUpdated": "Status updated",
      "configVersion": "Config version",
      "lastActivity": "Last activity",
      "title": "Agent",
      "title0": "Agents",
      "advancedlogMode": "Advanced logging mode",
      "periodicity": "Scan period, minutes",
      "threadCount": "Number of threads",
      "snapPacketSize": "Snapshot's packet size",
      "default": "By default",
      "create": {
        "hint": "Add new agent manually"
      },
      "delete": {
        "confirm": "Do you really wish to remove agent?"
      },
      "save": {
        "confirm": "Do you really wish to save agent?"
      }
    }
  },
  "ru": {
    "agent": {
      "id": "ID",
      "url": "URL",
      "token": "Токен",
      "status": "Статус",
      "location": "Локация",
      "statusUpdated": "Обновление статуса",
      "version": "Версия агента",
      "configVersion": "Версия конфига",
      "lastActivity": "Последняя активность",
      "title": "Агент",
      "title0": "Агенты",
      "advancedlogMode": "Расширенный режим логирования",
      "periodicity": "Периодичность опроса, минут",
      "threadCount": "Количество потоков",
      "snapPacketSize": "Количество снимков в пакете",
      "default": "По умолчанию",
      "create": {
        "hint": "Зарегистрировать нового агента вручную"
      },
      "delete": {
        "confirm": "Вы действительно хотите удалить агента?"
      },
      "save": {
        "confirm": "Вы действительно хотите сохранить информацию об агенте?"
      }
    }
  }
}
</i18n>

<template>
  <div>
    <dx-form
      v-if="agent"
      :key="id"
      id="form"
      label-location="left"
      :form-data="agent"
      :col-count="1"
      :read-only="disabledOnEditing"
      validation-group="agentValidation"
    >
      <dx-group-item :colCountByScreen="colCountByScreen">
        <dx-simple-item data-field="url" :editor-options="{maxLength: 500}">
          <dx-label :text="$t('agent.url')" :show-colon="false" />
          <dx-required-rule :message="$t('general.mandatory')" />
          <dx-validation-rule type="pattern" :pattern="urlPattern" :message="$t('general.badUrl')" />
        </dx-simple-item>

        <dx-simple-item data-field="securityToken" :editor-options="{maxLength: 36, disabled: isSecurityTokenDisabled}">
          <dx-label :text="$t('agent.token')" :show-colon="false" />
          <dx-required-rule :message="$t('general.mandatory')" />
        </dx-simple-item>

        <dx-simple-item data-field="statusText"
          :editor-options="agentStatusEditorOptions"
        >
          <dx-label :text="$t('agent.status')" :show-colon="false" />
        </dx-simple-item>

        <dx-simple-item data-field="location.id"
          editor-type="dxLookup"
          :editor-options="{
            dataSource:locationsItems, key:'id', displayExpr:'name', valueExpr:'id', searchEnabled:true,
            searchExpr:['name'], searchMode:'contains', minSearchLength:0, showPopupTitle: false, showCancelButton: false,
            dropDownOptions: {hideOnOutsideClick: true, showTitle: false}
          }"
        >
          <dx-label :text="$t('agent.location')" :show-colon="false" />
          <dx-required-rule :message="$t('general.mandatory')" />
        </dx-simple-item>

        <dx-simple-item data-field="statusUpdated" editor-type="dxDateBox"
          :editor-options="{ displayFormat: 'dd.MM.yyyy HH:mm:ss', readOnly: true }"
        >
          <dx-label :text="$t('agent.statusUpdated')" :show-colon="false" />
        </dx-simple-item>

        <dx-simple-item data-field="agentVersion"
          :editor-options="{ readOnly: true }"
        >
          <dx-label :text="$t('agent.version')" :show-colon="false" />
        </dx-simple-item>

        <dx-simple-item data-field="configVersion"
          :editor-options="{ readOnly: true }"
        >
          <dx-label :text="$t('agent.configVersion')" :show-colon="false" />
        </dx-simple-item>

        <dx-simple-item data-field="lastActivity" editor-type="dxDateBox"
          :editor-options="{ displayFormat: 'dd.MM.yyyy HH:mm:ss', readOnly: true }"
        >
          <dx-label :text="$t('agent.lastActivity')" :show-colon="false" />
        </dx-simple-item>

      </dx-group-item>

      <dx-group-item
        v-if="agent.agentSettings"
        caption="Расширенные настройки"
      >
        <dx-group-item v-for="setting in agent.agentSettings" :key="setting.id">
          <dx-simple-item
            v-if="isVisible(setting.setting.code)"
            :template="templateByCode(setting.setting.code)"
          >
            <dx-label :text="$t(labelKeyByCode(setting.setting.code))" />
            <dx-required-rule
              v-if="isRequired(setting.setting.code)"
              :message="$t('general.mandatory')"
            />
          </dx-simple-item>
        </dx-group-item>
      </dx-group-item>

      <template #advancedlogMode>
        <dx-check-box
          :value="agentAdvancedLogValue"
          :rtlEnabled="true"
          :read-only="disabledOnEditing"
          @valueChanged="onValueChangedEnabledAdvancedLog"
        />
      </template>

      <template #agentPeriodicityValue>
        <DxSelectBox
          :items="processesPeriodicity"
          :value="agentPeriodicityValue"
          :width="160"
          :read-only="disabledOnEditing"
          @valueChanged="onValueChangedPeriodicity"
        />
      </template>

      <template #threadCountValue>
        <DxSelectBox
          :items="threadCounts"
          :value="agentThreadCountValue"
          :width="160"
          :read-only="disabledOnEditing"
          @valueChanged="onValueChangedThreadCount"
        />
      </template>

      <template #snapPacketSize>
        <DxNumberBox
          :min="10"
          :max="500"
          :width="60"
          :value="snapPacketSizeValue"
          @valueChanged="onValueChangedSnapPacketSize"
        />
      </template>
    </dx-form>

    <div class="app-form-buttons">
      <start-stop-button v-if="agent && agent.status" :status="agent.status" :disabled="disabledOnEditing" @click="onStartStop" />

      <aims2-button
        icon="save"
        type="default"
        :text="$t('general.save')"
        :disabled="disabledOnEditing"
        :busy="saving"
        @click="onSave"
      />

      <aims2-button
        icon="trash"
        type="danger"
        :text="$t('general.delete')"
        :disabled="disabledDeleteButton"
        :busy="deleting"
        @click="onDelete"
      />
    </div>
  </div>
</template>

<script>
import baseView from '../base.vue';
import appInfo from '../../app-info';

import {
  DxForm,
  DxSimpleItem,
  DxLabel,
  DxGroupItem,
  DxRequiredRule,
  DxValidationRule
} from 'devextreme-vue/form';
import DxCheckBox from "devextreme-vue/check-box"
import DxSelectBox from 'devextreme-vue/select-box';
import { DxNumberBox } from "devextreme-vue/number-box";
import ValidationEngine from 'devextreme/ui/validation_engine';

import { notifier } from '@/shared/lib/notifier/index';

import { postAgent, putAgent, deleteAgent, setAgentStatus } from '@/services/api';
import { AclHandler } from "@/services/acl-handler";

import aims2Button from '@/components/active-button.vue';
import showConfirmDialog from '@/components/confirm-dialog';
import startStopButton from './start-stop-button';

import { DataLocation } from "@/mixins/dataLocationMixin";
import { formatAgentStatusColor, formatAgentStatusText, AgentStatus } from './constants';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faListOl } from '@fortawesome/free-solid-svg-icons';
library.add(faListOl);

export default {
  name: 'AgentDetail',
  props: ['id', 'agent', 'locationsItems'],
  extends: baseView,
  mixins: [DataLocation],
  components: {
    DxForm, DxSimpleItem, DxCheckBox, DxSelectBox, DxLabel, DxGroupItem, DxRequiredRule, DxValidationRule, aims2Button, startStopButton, DxNumberBox
  },
  data() {
    return {
      processesPeriodicity: [this.$t('agent.default'), 5, 10, 15, 20, 30, 60],
      threadCounts: [this.$t('agent.default'), 20, 50, 100, 150, 200, 250, 300],
      saving: false,
      deleting: false,
      colCountByScreen: {
        xs: 1,
        sm: 1,
        md: 2,
        lg: 2
      },
      urlPattern: /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/
    };
  },
  computed: {
    agentkId() {
      return Number(this.$route.params.id);
    },
    isNew() {
      return this.agentkId < 0;
    },
    appUser() {
      return this.$store.getters.user;
    },
    aclUser() {
      return new AclHandler(this.appUser, this.isCloudMode);
    },
    isDefaultAdmin() {
      return this.aclUser.hasPermission({ roleCodes : ['ADMIN', 'DEVELOPER'] }) && this.aclUser.hasDefaultLocation();
    },
    isSecurityTokenDisabled() {
      return this.isNew ? false : !this.isDefaultAdmin;
    },
    agentStatusEditorOptions() {
      const color = formatAgentStatusColor(this.agent.status);
      return {
        readOnly: true,
        inputAttr: { style: `color: ${color};` }
      };
    },
    // TODO
    agentProcessesPeriodicity() {
      return this.agent.agentSettings.find(s => s.setting.code === 'PROCESSES_PERIODICITY');
    },
    agentThreadCount() {
      return this.agent.agentSettings.find(s => s.setting.code === 'THREAD_COUNT');
    },
    agentAdvancedLog() {
      return this.agent.agentSettings.find(s => s.setting.code === 'ADVANCED_LOG');
    },
    snapPacketSize() {
      return this.agent.agentSettings.find(s => s.setting.code === 'SNAP_PACKET_SIZE');
    },
    agentPeriodicityValue() {
      if (!this.agentProcessesPeriodicity.numericValue || this.agentProcessesPeriodicity.numericValue === -1)
        return this.$t('agent.default');

      return (this.agentProcessesPeriodicity && this.agentProcessesPeriodicity.isEnabled) ? this.agentProcessesPeriodicity.numericValue : this.$t('agent.default');
    },
    agentThreadCountValue() {
      if (!this.agentThreadCount.numericValue || this.agentThreadCount.numericValue === -1)
        return this.$t('agent.default');
      else
        return (this.agentThreadCount && this.agentThreadCount.isEnabled) ? this.agentThreadCount.numericValue : this.$t('agent.default');
    },
    agentAdvancedLogValue() {
      return (this.agentAdvancedLog && this.agentAdvancedLog.isEnabled) ? this.agentAdvancedLog.flagValue : this.agentAdvancedLog.setting.flagValue;
    },
    snapPacketSizeValue() {
      return (this.snapPacketSize && this.snapPacketSize.isEnabled) ? this.snapPacketSize.numericValue : this.snapPacketSize.setting.numericValue;
    },
    disabledOnEditing() {
      return this.saving || this.deleting;
    },
    disabledDeleteButton() {
      return this.saving || this.deleting || !this.agent || !this.agent.id;
    }
  },
  methods: {
    isVisible(code) {
      return code === 'ADVANCED_LOG' ? this.isDefaultAdmin : true;
    },
    templateByCode(code) {
      switch (code) {
        case 'ADVANCED_LOG':
          return 'advancedlogMode';

        case 'PROCESSES_PERIODICITY':
          return 'agentPeriodicityValue';

        case 'THREAD_COUNT':
          return 'threadCountValue';

        case 'SNAP_PACKET_SIZE':
          return 'snapPacketSize';

        default:
          return null;
      }
    },
    labelKeyByCode(code) {
      switch (code) {
        case 'ADVANCED_LOG':
          return 'agent.advancedlogMode';

        case 'PROCESSES_PERIODICITY':
          return 'agent.periodicity';

        case 'THREAD_COUNT':
          return 'agent.threadCount';

        case 'SNAP_PACKET_SIZE':
          return 'agent.snapPacketSize';

        default:
          return '';
      }
    },
    isRequired(code) {
      const NOT_REQUIRED = ['ADVANCED_LOG'];

      return !NOT_REQUIRED.includes(code)
    },
    onValueChangedEnabledAdvancedLog(e) {
      this.agentAdvancedLog.isEnabled = e.value;
      this.agentAdvancedLog.flagValue = e.value;
    },
    onValueChangedThreadCount(e) {
      if (typeof e.value === 'string') {
        this.agentThreadCount.numericValue = -1;
        this.agentThreadCount.isEnabled = false;
      } else {
        this.agentThreadCount.numericValue = e.value;
        this.agentThreadCount.isEnabled = true;
      }
    },
    onValueChangedPeriodicity(e) {
      if (typeof e.value === 'string') {
        this.agentProcessesPeriodicity.numericValue = -1;
        this.agentProcessesPeriodicity.isEnabled = false;
      } else {
        this.agentProcessesPeriodicity.numericValue = e.value;
        this.agentProcessesPeriodicity.isEnabled = true;
      }
    },
    onValueChangedSnapPacketSize(e) {
      this.snapPacketSize.numericValue = e.value;
      this.snapPacketSize.isEnabled = true;
    },
    validate() {
      return ValidationEngine.validateGroup("agentValidation");
    },
    async onSave() {
      const validate = this.validate();
      if (!validate.isValid) {
        validate.brokenRules[0].validator.focus();  
        return;
      }

      const response = await showConfirmDialog({
        title: this.$t('general.save'),
        message: this.$t('agent.save.confirm'),
        btnType: 'default',
        okButton: this.$t('general.proceed'),
        cancelButton: this.$t('general.cancel'),
      });

      if (!response) return;
      try {
        this.saving = true;
        if (this.agent.id) {
          await putAgent(this.agent.id, this.agent);
          this.saving = false;
          notifier.showSuccess(this.$t('general.updateSuccess'));
        } else {
          postAgent(this.agent)
          .then(agent => {
            this.$router.replace({
              name: "agent",
              params: { id: agent.id }
            })
            this.saving = false;
            notifier.showSuccess(this.$t('general.createSuccess'));
          })
          .catch((err) => {
            notifier.showError(err);
            this.saving = false;
          });
        }
      } catch(err) {
        this.onDataError(err);
        this.saving = false;
      }
    },
    async onDelete() {
      const response = await showConfirmDialog({
        title: this.$t('general.delete'),
        message: this.$t('agent.delete.confirm'),
        btnType: 'default',
        okButton: this.$t('general.proceed'),
        cancelButton: this.$t('general.cancel'),
      });

      if (!response) return;
      try {
        this.deleting = true;
        await deleteAgent(this.agent.id);
        notifier.showSuccess(this.$t('general.deleteSuccess'));
        setTimeout(() => this.$router.go(-1));
      } catch(err) {
        this.onDataError(err);
      } finally {
        this.deleting = false;
      }
    },
    async onStartStop() {
      // alert('onStartStop');
      switch (this.agent.status) {
        case AgentStatus.IDLE.id:
          {
            this.agent.status = AgentStatus.STARTING.id;
            this.agent.statusText = formatAgentStatusText(this.agent.status);
            try {
              await setAgentStatus(this.agent.id, AgentStatus.STARTING.id);
              this.agent.status = AgentStatus.RUNNING.id;
            } catch (err) {
              this.agent.status = AgentStatus.ERROR.id;
              this.onDataError(err);
            }
            this.agent.statusText = formatAgentStatusText(this.agent.status);
          }
          break;
        case AgentStatus.RUNNING.id:
        case AgentStatus.ERROR.id:
          {
            this.agent.status = AgentStatus.STOPPING.id;
            this.agent.statusText = formatAgentStatusText(this.agent.status);
            try {
              await setAgentStatus(this.agent.id, AgentStatus.STOPPING.id);
              this.agent.status = AgentStatus.IDLE.id;
            } catch (err) {
              this.agent.status = AgentStatus.ERROR.id;
              this.onDataError(err);
            }
            this.agent.statusText = formatAgentStatusText(this.agent.status);
          }
          break;
      }
    },
    onLog() {
      const filter = JSON.stringify([['objectClass', '=', 'AgentDao'], 'and', ['objectId', '=', this.agent.id]]);
      setTimeout(() => this.$router.push({
        name: "logs",
        query: { filter }
      }));
    }
  },
  async created() {
    document.title = `${appInfo.title} - ${this.$t('agent.title')}`;
  }
};
</script>

<style lang="scss">
</style>
